import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Modal } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  add_selected_competitors,
  get_intelligence_dashboard,
  get_sync_status,
  get_theme_evidence,
  regenerate_content_cards,
  remove_competitor_search,
  search_competitors,
  server_post_data,
  trigger_sync,
  update_competitor,
  update_content_card,
  update_recommendation,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import {
  compact, cssVar, fmtDate, makeCaller, relativeTime, signed, stars,
} from "../CommonJquery/format";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/intelligence-dashboard.css";

const SECTIONS = [
  { key: "health", label: "How you're doing", icon: "mdi:store-outline" },
  { key: "voice", label: "What customers say", icon: "mdi:comment-text-outline" },
  { key: "tasks", label: "Fix these first", icon: "mdi:clipboard-check-outline" },
  { key: "posts", label: "Make posts", icon: "mdi:instagram" },
  { key: "compare", label: "Nearby businesses", icon: "mdi:map-marker-radius-outline" },
];

const TONE_ICON = {
  good: "mdi:check-circle",
  watch: "mdi:alert-circle-outline",
  bad: "mdi:alert-octagon-outline",
  info: "mdi:information-outline",
};

const bandFor = (n) => (n >= 70 ? "good" : n >= 45 ? "watch" : "bad");

/* Complaint rate to heat colour. Low rates stay nearly transparent so the eye
   lands on genuinely hot cells rather than a wall of red. */
const heatColor = (rate) => {
  if (!rate) return "var(--gmb-paper)";
  const intensity = Math.min(rate / 25, 1);
  return `rgba(227, 10, 10, ${0.08 + intensity * 0.55})`;
};

/* ==========================================================================
   FINDING — the repeated unit: what's happening, why, what to do
   ========================================================================== */
function Finding({ f, onEvidence }) {
  return (
    <div className={`in-finding ${f.tone}`}>
      <span className={`in-finding-icon bg-${f.tone}`}>
        <Icon icon={f.icon || TONE_ICON[f.tone]} />
      </span>
      <div className="in-finding-body">
        <div className="in-finding-headline">{f.headline}</div>
        <div className="in-finding-detail">{f.detail}</div>

        {f.action && (
          <div className="in-finding-action">
            <Icon icon="mdi:arrow-right-circle-outline" style={{ fontSize: 15 }} />
            <div>
              <b>What to do</b>
              {f.action}
            </div>
          </div>
        )}

        {f.stat && (
          <div className="in-finding-stat">
            <b>{f.stat.value}</b>
            <span>{f.stat.label}</span>
          </div>
        )}

        {f.theme && onEvidence && (
          <button className="in-finding-link" onClick={() => onEvidence(f.theme)}>
            Read what customers actually wrote →
          </button>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   DASHBOARD
   ========================================================================== */
function IntelligenceDashboard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeSection, setActiveSection] = useState("health");
  const [themeTick, setThemeTick] = useState(0);

  const [keyword, setKeyword] = useState("");
  const [radius, setRadius] = useState(5000);

  /* Search results live in component state and are never persisted until the
     owner picks. `picked` holds place ids rather than indexes so the selection
     survives the list being re-sorted or re-fetched. */
  const [results, setResults] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const [picked, setPicked] = useState(() => new Set());

  const [contentTheme, setContentTheme] = useState(null);
  const [preview, setPreview] = useState(null);

  const [evidence, setEvidence] = useState(null);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState(false);

  /* The analytical detail is opt-in. Owners who want the working can open it;
     owners who don't are never asked to interpret a chart. */
  const [openDetail, setOpenDetail] = useState({});

  const busyRef = useRef(false);
  const pollRef = useRef(null);
  const call = useMemo(() => makeCaller(server_post_data, handleError), []);

  const load = useCallback(
    async (opts = {}) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setLoading(true);

      const d = await call(get_intelligence_dashboard, {
        days: 90,
        content_theme: opts.contentTheme ?? contentTheme ?? "",
      });
      if (d) setData(d);

      setLoading(false);
      busyRef.current = false;
    },
    [call, contentTheme],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Charts read colours from CSS variables, so a theme switch has to force a
     re-render for them to pick up the new values. */
  useEffect(() => {
    const obs = new MutationObserver(() => setThemeTick((t) => t + 1));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);

  /* Highlight the section in view rather than the one last clicked, so the nav
     stays honest when someone scrolls instead of jumping. */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id.replace("sec-", ""));
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: [0.1, 0.5] },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(`sec-${s.key}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [data]);

  /* While a sync runs there is nothing new to show until it finishes, so this
     polls the cheap status endpoint and reloads once. */
  const pollSync = useCallback(async () => {
    const d = await call(get_sync_status, {
      job_key: "competitor_sync",
    });
    if (!d?.sync) return;

    setData((prev) =>
      prev ? { ...prev, sync: { ...prev.sync, competitor: d.sync } } : prev,
    );

    if (d.sync.isRunning) {
      pollRef.current = setTimeout(pollSync, 5000);
    } else {
      pollRef.current = null;
      handleSuccess("Finished checking nearby businesses");
      load();
    }
  }, [call, load]);

  useEffect(() => {
    if (data?.sync?.competitor?.isRunning && !pollRef.current) {
      pollRef.current = setTimeout(pollSync, 5000);
    }
    return () => {
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [data?.sync?.competitor?.isRunning, pollSync]);

  /* ------------------------- actions ------------------------- */
  const jumpTo = (key) => {
    const el = document.getElementById(`sec-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleDetail = (key) =>
    setOpenDetail((prev) => ({ ...prev, [key]: !prev[key] }));

  const runSearch = async () => {
    if (!keyword.trim()) {
      handleError("Type what your business is, like cafe or salon");
      return;
    }
    setLoading(true);
    const d = await call(search_competitors, {
      keyword: keyword.trim(),
      radius_meters: radius,
    });
    setLoading(false);
    if (d) {
      setResults(d.results || []);
      setSearchMeta(d);
      // Pre-tick the obvious ones so an owner who trusts the search can add in
      // two clicks, while closed and near-reviewless places start unticked.
      setPicked(
        new Set((d.results || []).filter((r) => r.suggested).map((r) => r.placeId)),
      );
    }
  };

  const togglePick = (placeId) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });

  const pickAllSuggested = () =>
    setPicked(
      new Set((results || []).filter((r) => r.suggested).map((r) => r.placeId)),
    );

  const clearSearch = () => {
    setResults(null);
    setSearchMeta(null);
    setPicked(new Set());
  };

  const addSelected = async () => {
    if (picked.size === 0) {
      handleError("Tick at least one business first");
      return;
    }
    setLoading(true);
    const d = await call(add_selected_competitors, {
      keyword: searchMeta?.keyword || keyword.trim(),
      radius_meters: radius,
      place_ids: Array.from(picked).join(","),
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      clearSearch();
      setKeyword("");
      load();
    }
  };

  const dropSearch = async (searchId) => {
    setLoading(true);
    const d = await call(remove_competitor_search, {
      search_id: searchId,
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      load();
    }
  };

  const dropCompetitor = async (id) => {
    setLoading(true);
    const d = await call(update_competitor, {
      competitor_id: id,
      action: "delete",
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      load();
    }
  };

  const syncNow = async () => {
    setLoading(true);
    const d = await call(trigger_sync, {   });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      setData((prev) =>
        prev ? { ...prev, sync: { ...prev.sync, competitor: d.sync } } : prev,
      );
    }
  };

  const pickContentTheme = (key) => {
    const next = contentTheme === key ? null : key;
    setContentTheme(next);
    load({ contentTheme: next ?? "" });
  };

  const updateCard = async (cardId, action) => {
    setLoading(true);
    const d = await call(update_content_card, {
      card_id: cardId,
      action,
    });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      load();
    }
  };

  /* Copying is what almost everyone does, so it also marks the post used —
     otherwise the same quote resurfaces next week. */
  const copyCard = (card) => {
    navigator.clipboard.writeText(`${card.caption}\n\n${card.hashtags}`);
    handleSuccess("Copied — paste it into Instagram or WhatsApp");
    updateCard(card.id, "used");
  };

  const rescanContent = async () => {
    setLoading(true);
    const d = await call(regenerate_content_cards, {  });
    setLoading(false);
    if (d) {
      handleSuccess(d.message);
      load();
    }
  };

  const completeTask = async (task, action) => {
    if (!task.recommendationId) return;
    setLoading(true);
    const d = await call(update_recommendation, {
      recommendation_id: task.recommendationId,
      action,
    });
    setLoading(false);
    if (d) {
      handleSuccess(action === "done" ? "Nice — marked as done" : d.message);
      load();
    }
  };

  const openEvidence = async (theme) => {
    setEvidence(null);
    setShowEvidence(true);
    setLoading(true);
    const d = await call(get_theme_evidence, {
      theme,
      limit: 25,
    });
    setEvidence(d);
    setLoading(false);
  };

  /* ------------------------- charts ------------------------- */
  const ink = cssVar("--text-secondary-light", "#6b7280");
  const line = cssVar("--border-color", "#d1d5db80");

  const trendChart = useMemo(() => {
    const points = data?.health?.history?.points || [];
    return {
      series: [{ name: "Score", data: points.map((p) => ({ x: p.date, y: p.score })) }],
      options: {
        chart: {
          type: "area", height: 230, toolbar: { show: false },
          zoom: { enabled: false }, fontFamily: "inherit", background: "transparent",
        },
        colors: [cssVar("--primary-600", "#0e71fa")],
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95] },
        },
        dataLabels: { enabled: false },
        grid: { borderColor: line, strokeDashArray: 4 },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: ink, fontSize: "11px" } },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { min: 0, max: 100, tickAmount: 4, labels: { style: { colors: ink, fontSize: "11px" } } },
        tooltip: { x: { format: "dd MMM yyyy" } },
      },
    };
  }, [data, ink, line, themeTick]);

  const sentimentChart = useMemo(() => {
    const weekly = data?.health?.weekly || [];
    return {
      series: [
        { name: "Happy customers", data: weekly.map((w) => ({ x: w.weekStart, y: w.positive })) },
        { name: "Unhappy customers", data: weekly.map((w) => ({ x: w.weekStart, y: -w.negative })) },
      ],
      options: {
        chart: {
          type: "bar", height: 230, stacked: true, toolbar: { show: false },
          fontFamily: "inherit", background: "transparent",
        },
        colors: [cssVar("--success-main", "#16a34a"), cssVar("--danger-main", "#e30a0a")],
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        grid: { borderColor: line, strokeDashArray: 4 },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: ink, fontSize: "11px" } },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: ink, fontSize: "11px" },
            formatter: (v) => Math.abs(Math.round(v)),
          },
        },
        legend: { position: "top", horizontalAlign: "right", labels: { colors: ink } },
        tooltip: { x: { format: "dd MMM" }, y: { formatter: (v) => `${Math.abs(v)} reviews` } },
      },
    };
  }, [data, ink, line, themeTick]);

  /* ------------------------- derived ------------------------- */
  const advice = data?.advice;
  const health = data?.health;
  const content = data?.content;
  const radar = data?.radar;
  const comparison = data?.comparison;
  const sync = data?.sync?.competitor;

  const inputs = health?.inputs;
  const spreadMax = inputs
    ? Math.max(...Object.values(inputs.ratingSpread || {}), 1)
    : 1;
  const scoreChange = health?.history?.changeOverPeriod;

  const openTasks = (advice?.tasks || []).filter((t) => !t.isDone);
  const doneTasks = (advice?.tasks || []).filter((t) => t.isDone);

  const navCounts = {
    health: 0,
    voice: (advice?.customerVoice?.hurting?.length || 0),
    tasks: openTasks.length,
    posts: content?.summary?.ready || 0,
    compare: radar?.competitors?.length || 0,
  };

  return (
    <MasterLayout>
      <div className="gmb-premium intel intel-page">
        {loading && <Loader />}

        {/* ==================== TOPBAR ==================== */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Your Business Report</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:store-check-outline" /> Updated{" "}
              {data?.generatedAt ? relativeTime(data.generatedAt) : "now"}
            </span>
          </div>
          <div className="gmb-topbar-actions">
            <button className="btn btn-outline-primary-600" onClick={() => load()}>
              <Icon icon="mdi:refresh" /> Refresh
            </button>
          </div>
        </div>

        {!data ? (
          <>
            <div className="in-skeleton in-skeleton-answers" />
            <div className="in-skeleton in-skeleton-hero" />
            <div className="row g-3">
              <div className="col-lg-6"><div className="in-skeleton in-skeleton-panel" /></div>
              <div className="col-lg-6"><div className="in-skeleton in-skeleton-panel" /></div>
            </div>
          </>
        ) : (
          <>
            {/* ==================== QUICK ANSWERS ==================== */}
            <div className="in-answers">
              {advice.quickAnswers.map((a) => (
                <button
                  className="in-answer"
                  key={a.key}
                  onClick={() => jumpTo(a.jumpTo)}
                >
                  <span className={`in-answer-icon bg-${a.tone}`}>
                    <Icon icon={a.icon} />
                  </span>
                  <span className="in-answer-body">
                    <span className="in-answer-q">{a.question}</span>
                    <span className={`in-answer-a tone-${a.tone}`}>{a.answer}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* ==================== NAV ==================== */}
            <nav className="in-nav">
              {SECTIONS.map((s) => (
                <button
                  key={s.key}
                  className={activeSection === s.key ? "active" : ""}
                  onClick={() => jumpTo(s.key)}
                >
                  <Icon icon={s.icon} />
                  {s.label}
                  {navCounts[s.key] > 0 && (
                    <span className="in-nav-count">{navCounts[s.key]}</span>
                  )}
                </button>
              ))}
            </nav>

            {/* ================================================================
                1. HOW YOU'RE DOING
               ================================================================ */}
            <section className="in-section" id="sec-health">
              <div className={`in-verdict ${advice.verdict.tone}`}>
                <span className={`in-verdict-face bg-${advice.verdict.tone}`}>
                  <Icon icon={advice.verdict.icon} />
                </span>

                <div className="in-verdict-body">
                  <div className={`in-verdict-label tone-${advice.verdict.tone}`}>
                    {advice.verdict.label}
                  </div>
                  <div className="in-verdict-headline">
                    {advice.verdict.headline}
                  </div>
                  <div className="in-verdict-detail">{advice.verdict.detail}</div>

                  {advice.verdict.action && (
                    <div className="in-verdict-action">
                      <Icon icon="mdi:arrow-right-circle-outline" style={{ fontSize: 16 }} />
                      <span>{advice.verdict.action}</span>
                    </div>
                  )}
                </div>

                <div className="in-verdict-score">
                  <b>{Math.round(advice.verdict.score)}</b>
                  <small>out of 100</small>
                  {scoreChange !== null && scoreChange !== undefined && (
                    <span
                      className={`in-score-delta bg-${
                        scoreChange > 1 ? "good" : scoreChange < -1 ? "bad" : "info"
                      }`}
                    >
                      <Icon
                        icon={
                          scoreChange > 1
                            ? "mdi:trending-up"
                            : scoreChange < -1
                              ? "mdi:trending-down"
                              : "mdi:trending-neutral"
                        }
                      />
                      {signed(scoreChange)} this month
                    </span>
                  )}
                </div>
              </div>

              {/* ---- see the numbers ---- */}
              <button
                className="in-detail-toggle"
                onClick={() => toggleDetail("health")}
              >
                <Icon icon={openDetail.health ? "mdi:chevron-up" : "mdi:chevron-down"} />
                {openDetail.health ? "Hide the numbers" : "See how this score is worked out"}
              </button>

              {openDetail.health && (
                <div className="in-detail-panel">
                  <div className="row g-3 mb-3">
                    <div className="col-xl-7">
                      <div className="gmb-card in-panel">
                        <div className="in-panel-title">What goes into the score</div>
                        <div className="in-panel-sub">
                          Five things, each out of 100. Greyed lines could not be
                          measured yet — they are left out rather than counted
                          against you.
                        </div>

                        {health.components.map((c) => (
                          <div
                            className={`in-comp-bar ${c.available ? "" : "unavailable"}`}
                            key={c.key}
                          >
                            <div className="in-comp-bar-body">
                              <div className="in-comp-bar-name">
                                {c.label}
                                <span className="in-chip-mini">
                                  {c.available ? `counts for ${c.weight}%` : "not measured yet"}
                                </span>
                              </div>
                              {c.available ? (
                                <div className="in-track">
                                  <span
                                    className={`in-fill ${bandFor(c.score)}`}
                                    style={{ width: `${c.score}%` }}
                                  />
                                </div>
                              ) : (
                                <div className="in-panel-sub" style={{ margin: 0 }}>
                                  {c.unavailableReason}
                                </div>
                              )}
                            </div>
                            <div className="in-comp-bar-score">
                              <b>{c.available ? Math.round(c.score) : "—"}</b>
                              {c.available && <small>of 100</small>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-xl-5">
                      <div className="gmb-card in-panel">
                        <div className="in-panel-title">Your star ratings</div>
                        <div className="in-panel-sub">
                          How your {compact(inputs.reviewCount)} reviews are spread.
                        </div>
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count = inputs.ratingSpread?.[star] || 0;
                          return (
                            <div className="in-spread-row" key={star}>
                              <span className="in-spread-label">{star} ★</span>
                              <span className="in-spread-track">
                                <span
                                  className={`in-spread-fill s${star}`}
                                  style={{ width: `${(count / spreadMax) * 100}%` }}
                                />
                              </span>
                              <span className="in-spread-count">
                                {count}
                                {inputs.reviewCount > 0 && (
                                  <span style={{ color: "var(--gmb-ink-3)" }}>
                                    {" "}
                                    {Math.round((count / inputs.reviewCount) * 100)}%
                                  </span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-xl-6">
                      <div className="gmb-card in-panel">
                        <div className="in-panel-title">Score over time</div>
                        <div className="in-panel-sub">
                          One reading tells you little. The line tells you whether
                          what you changed is working.
                        </div>
                        {!health.history?.hasHistory ? (
                          <div className="gmb-empty">
                            This appears once we have a few days of history.
                          </div>
                        ) : (
                          <ReactApexChart
                            key={`trend-${themeTick}`}
                            options={trendChart.options}
                            series={trendChart.series}
                            type="area"
                            height={230}
                          />
                        )}
                      </div>
                    </div>

                    <div className="col-xl-6">
                      <div className="gmb-card in-panel">
                        <div className="in-panel-title">Happy vs unhappy, week by week</div>
                        <div className="in-panel-sub">
                          If the red half is growing, something changed recently.
                        </div>
                        {!health.weekly?.length ? (
                          <div className="gmb-empty">Not enough history yet.</div>
                        ) : (
                          <ReactApexChart
                            key={`sent-${themeTick}`}
                            options={sentimentChart.options}
                            series={sentimentChart.series}
                            type="bar"
                            height={230}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ================================================================
                2. WHAT CUSTOMERS SAY
               ================================================================ */}
            <section className="in-section" id="sec-voice">
              <div className="in-section-head">
                <div>
                  <div className="in-section-title">
                    <span className="in-section-icon">
                      <Icon icon="mdi:comment-text-outline" />
                    </span>
                    What Your Customers Are Saying
                  </div>
                  <div className="in-section-sub">
                    We read the written part of your reviews and grouped what
                    people keep mentioning. Based on{" "}
                    {advice.customerVoice.analysedReviews} reviews with comments.
                  </div>
                </div>
              </div>

              {advice.customerVoice.note ? (
                <div className="in-empty-block">
                  <div className="in-empty-icon">
                    <Icon icon="mdi:comment-outline" />
                  </div>
                  <b>Nothing clear yet</b>
                  <p>{advice.customerVoice.note}</p>
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-lg-6">
                    <div className="in-panel-title mb-2">
                      <Icon icon="mdi:thumb-up-outline" className="tone-good" />{" "}
                      What customers like
                    </div>
                    {advice.customerVoice.working.length === 0 ? (
                      <div className="gmb-empty">
                        No clear pattern of praise yet.
                      </div>
                    ) : (
                      <div className="row g-3">
                        {advice.customerVoice.working.map((f) => (
                          <div className="col-12" key={f.id}>
                            <Finding f={f} onEvidence={openEvidence} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="col-lg-6">
                    <div className="in-panel-title mb-2">
                      <Icon icon="mdi:thumb-down-outline" className="tone-bad" />{" "}
                      What customers complain about
                    </div>
                    {advice.customerVoice.hurting.length === 0 ? (
                      <div className="gmb-empty">
                        No repeated complaints. That is a good sign.
                      </div>
                    ) : (
                      <div className="row g-3">
                        {advice.customerVoice.hurting.map((f) => (
                          <div className="col-12" key={f.id}>
                            <Finding f={f} onEvidence={openEvidence} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---- recent reviews, behind the toggle ---- */}
              <button
                className="in-detail-toggle"
                onClick={() => toggleDetail("recent")}
              >
                <Icon icon={openDetail.recent ? "mdi:chevron-up" : "mdi:chevron-down"} />
                {openDetail.recent
                  ? "Hide recent reviews"
                  : `See the ${health.recent.newReviews} reviews from the last two weeks`}
              </button>

              {openDetail.recent && (
                <div className="in-detail-panel">
                  <div className="gmb-card in-panel">
                    <div className="in-panel-title">Last two weeks</div>
                    <div className="in-panel-sub">
                      {health.recent.newNegatives} unhappy,{" "}
                      {health.recent.unanswered} still waiting for your reply.
                    </div>
                    {!health.recent.items?.length ? (
                      <div className="gmb-empty">No new reviews recently.</div>
                    ) : (
                      health.recent.items.map((r) => (
                        <div className="in-recent" key={r.id}>
                          <span className={`in-recent-badge bg-${r.kind === "positive" ? "good" : "bad"}`}>
                            {r.rating}
                          </span>
                          <div className="in-recent-body">
                            <p>{r.text}</p>
                            <div className="in-recent-meta">
                              <span>{r.author}</span>
                              <span>{fmtDate(r.date)}</span>
                              {r.kind === "negative" && !r.hasReply && (
                                <span className="in-noreply">Needs a reply</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* ================================================================
                3. FIX THESE FIRST
               ================================================================ */}
            <section className="in-section" id="sec-tasks">
              <div className="in-section-head">
                <div>
                  <div className="in-section-title">
                    <span className="in-section-icon">
                      <Icon icon="mdi:clipboard-check-outline" />
                    </span>
                    Fix These First
                  </div>
                  <div className="in-section-sub">
                    In order. The first one will help your rating more than the
                    rest. Each is based on something customers actually wrote —
                    we do not suggest anything we cannot show you evidence for.
                  </div>
                </div>
              </div>

              {openTasks.length === 0 ? (
                <div className="in-empty-block">
                  <div className="in-empty-icon">
                    <Icon icon="mdi:check-circle-outline" />
                  </div>
                  <b>Nothing urgent right now</b>
                  <p>
                    {data.recommendationSummary?.note ||
                      "Keep doing what you are doing. We will tell you when something changes."}
                  </p>
                </div>
              ) : (
                openTasks.map((t, i) => (
                  <div className={`in-task ${t.tone}`} key={t.id}>
                    <span className="in-task-num">{i + 1}</span>
                    <div className="in-task-body">
                      <div className="in-task-head">
                        <span className="in-task-headline">{t.headline}</span>
                        <span className="in-effort">{t.effort}</span>
                      </div>
                      <div className="in-task-detail">{t.detail}</div>

                      <div className="in-task-action">
                        <Icon icon="mdi:arrow-right-circle-outline" style={{ fontSize: 16 }} />
                        <div>
                          <b>What to do</b>
                          {t.action}
                        </div>
                      </div>

                      <div className="in-task-foot">
                        <span className="in-task-stat">
                          <b>{t.stat.value}</b> {t.stat.label}
                        </span>
                        {t.theme && (
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openEvidence(t.theme)}
                          >
                            <Icon icon="mdi:comment-quote-outline" /> See the reviews
                          </button>
                        )}
                        {t.recommendationId && (
                          <>
                            <button
                              className="btn btn-primary-600 btn-sm"
                              onClick={() => completeTask(t, "done")}
                            >
                              <Icon icon="mdi:check" /> Done
                            </button>
                            <button
                              className="btn btn-outline-danger-600 btn-sm"
                              onClick={() => completeTask(t, "dismiss")}
                              title="Not relevant to my business"
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              {doneTasks.length > 0 && (
                <>
                  <button
                    className="in-detail-toggle"
                    onClick={() => toggleDetail("done")}
                  >
                    <Icon icon={openDetail.done ? "mdi:chevron-up" : "mdi:chevron-down"} />
                    {doneTasks.length} already done
                  </button>
                  {openDetail.done && (
                    <div className="in-detail-panel">
                      {doneTasks.map((t) => (
                        <div className={`in-task ${t.tone} done`} key={t.id}>
                          <span className="in-task-num">
                            <Icon icon="mdi:check" />
                          </span>
                          <div className="in-task-body">
                            <div className="in-task-head">
                              <span className="in-task-headline">{t.headline}</span>
                            </div>
                            {t.recommendationId && (
                              <button
                                className="btn btn-outline-primary-600 btn-sm"
                                onClick={() => completeTask(t, "reopen")}
                              >
                                <Icon icon="mdi:undo" /> Move back
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>

            {/* ================================================================
                4. MAKE POSTS
               ================================================================ */}
            <section className="in-section" id="sec-posts">
              <div className="in-section-head">
                <div>
                  <div className="in-section-title">
                    <span className="in-section-icon">
                      <Icon icon="mdi:instagram" />
                    </span>
                    Turn Your Reviews Into Posts
                  </div>
                  <div className="in-section-sub">
                    Your happiest customers already wrote your marketing for you.
                    Copy any of these straight into Instagram, Facebook, WhatsApp
                    Status or your Google Business profile.
                  </div>
                </div>
                <div className="in-section-actions">
                  <button className="btn btn-outline-primary-600 btn-sm" onClick={rescanContent}>
                    <Icon icon="mdi:magnify-scan" /> Look for new ones
                  </button>
                </div>
              </div>

              <div className="in-posts-lead">
                <div className="in-posts-lead-body">
                  <b>
                    {content.summary.ready > 0
                      ? `${content.summary.ready} ready to post right now`
                      : "Nothing ready to post yet"}
                  </b>
                  <p>
                    We pick real sentences from your 4 and 5 star reviews. Nothing
                    is rewritten — these are your customers' own words. Reviews
                    with phone numbers, emails or someone's name are left out
                    automatically.
                  </p>
                  <div className="in-where">
                    <span><Icon icon="mdi:instagram" /> Instagram</span>
                    <span><Icon icon="mdi:facebook" /> Facebook</span>
                    <span><Icon icon="mdi:whatsapp" /> WhatsApp</span>
                    <span><Icon icon="mdi:google" /> Google Business</span>
                  </div>
                </div>
                <div className="in-posts-count">
                  <b>{content.summary.ready}</b>
                  <small>ready</small>
                </div>
              </div>

              {content.weeklyPlan?.length > 0 && (
                <div className="gmb-card in-panel mb-3">
                  <div className="in-panel-title">A plan for this week</div>
                  <div className="in-panel-sub">
                    Spread across different topics, so your feed does not read
                    like the same post five times.
                  </div>
                  {content.weeklyPlan.map((p) => (
                    <div className="in-plan-row" key={p.cardId}>
                      <span className="in-plan-day">{p.slot}</span>
                      <div className="in-plan-body">
                        <div className="in-plan-theme">
                          <Icon icon={p.icon} /> {p.theme}
                        </div>
                        <div className="in-plan-quote">{p.quote}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {content.summary?.themes?.length > 0 && (
                <div className="in-chips">
                  <button
                    className={`in-chip ${!contentTheme ? "active" : ""}`}
                    onClick={() => pickContentTheme(null)}
                  >
                    <Icon icon="mdi:apps" /> All
                    <span className="in-chip-count">{content.summary.ready}</span>
                  </button>
                  {content.summary.themes.map((t) => (
                    <button
                      key={t.key}
                      className={`in-chip ${contentTheme === t.key ? "active" : ""}`}
                      onClick={() => pickContentTheme(t.key)}
                    >
                      <Icon icon={t.icon} /> {t.short}
                      <span className="in-chip-count">{t.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {content.cards.length === 0 ? (
                <div className="in-empty-block">
                  <div className="in-empty-icon">
                    <Icon icon="mdi:comment-quote-outline" />
                  </div>
                  <b>Nothing ready yet</b>
                  <p>
                    {content.summary?.note ||
                      "Posts appear here when customers leave 4 or 5 star reviews with a written comment."}
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {content.cards.map((c) => (
                    <div className="col-xl-4 col-lg-6" key={c.id}>
                      <div className={`in-card ${c.isUsed ? "used" : ""}`}>
                        {c.isUsed && <span className="in-used-badge">Posted</span>}

                        <div className="in-card-head">
                          <span className="in-card-theme">
                            <Icon icon={c.icon} /> {c.short}
                          </span>
                          <span className="in-card-stars">{stars(c.rating)}</span>
                        </div>

                        <div className="in-quote">{c.quote}</div>

                        <div className="in-attribution">
                          — {c.attribution || "A happy customer"}
                          <span>{fmtDate(c.reviewDate)}</span>
                        </div>

                        <div className="in-card-actions">
                          <button
                            className="btn btn-primary-600 in-primary"
                            onClick={() => copyCard(c)}
                          >
                            <Icon icon="mdi:content-copy" /> Copy this post
                          </button>
                          <button
                            className="btn btn-outline-primary-600"
                            onClick={() => setPreview(c)}
                            title="See the full post"
                          >
                            <Icon icon="mdi:eye-outline" />
                          </button>
                          {c.isUsed ? (
                            <button
                              className="btn btn-outline-primary-600"
                              onClick={() => updateCard(c.id, "restore")}
                              title="Move back to ready"
                            >
                              <Icon icon="mdi:undo" />
                            </button>
                          ) : (
                            <button
                              className="btn btn-outline-danger-600"
                              onClick={() => updateCard(c.id, "dismiss")}
                              title="Don't show this one"
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ================================================================
                5. NEARBY BUSINESSES
               ================================================================ */}
            <section className="in-section" id="sec-compare">
              <div className="in-section-head">
                <div>
                  <div className="in-section-title">
                    <span className="in-section-icon">
                      <Icon icon="mdi:map-marker-radius-outline" />
                    </span>
                    How You Compare With Nearby Businesses
                  </div>
                  <div className="in-section-sub">
                    We read the public reviews of businesses near you and compare
                    them with yours. Type what you are — cafe, salon, gym — and
                    we will find them.
                  </div>
                </div>
                <div className="in-section-actions">
                  <button
                    className="btn btn-outline-primary-600 btn-sm"
                    onClick={syncNow}
                    disabled={sync?.isRunning}
                  >
                    <Icon icon="mdi:cloud-sync-outline" />
                    {sync?.isRunning ? "Checking..." : "Check again"}
                  </button>
                  {radar.competitors.length > 0 && (
                    <button
                      className="btn btn-outline-primary-600 btn-sm"
                      onClick={() => setShowCompetitors(true)}
                    >
                      <Icon icon="mdi:format-list-bulleted" /> Edit the list
                    </button>
                  )}
                </div>
              </div>

              {/* ---- search ---- */}
              <div className="in-search-bar">
                <div style={{ flex: 2, minWidth: 200 }}>
                  <label>What kind of business are you?</label>
                  <input
                    className="form-control"
                    placeholder="Cafe, salon, gym — or a specific shop name"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  />
                </div>
                <div>
                  <label>How far around you?</label>
                  <select
                    className="form-control"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                  >
                    <option value={1000}>1 km</option>
                    <option value={2000}>2 km</option>
                    <option value={5000}>5 km</option>
                    <option value={10000}>10 km</option>
                  </select>
                </div>
                <button className="btn btn-primary-600" onClick={runSearch}>
                  <Icon icon="mdi:magnify" /> Search
                </button>
                <div className="in-search-hint">
                  Search by category to find everyone nearby, or type a
                  specific shop's name to add just that one. Nothing is added
                  until you tick it, and each search adds to your list rather
                  than replacing it.
                </div>
              </div>

              {/* ---- results picker ---- */}
              {results && (
                <div className="in-results">
                  <div className="in-results-head">
                    <div>
                      <b>
                        {results.length} found for "{searchMeta?.keyword}"
                      </b>
                      <span>{searchMeta?.note}</span>
                    </div>
                    <div className="in-results-actions">
                      <button
                        className="btn btn-outline-primary-600 btn-sm"
                        onClick={pickAllSuggested}
                      >
                        Tick suggested ({searchMeta?.suggestedCount || 0})
                      </button>
                      <button
                        className="btn btn-outline-primary-600 btn-sm"
                        onClick={clearSearch}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary-600 btn-sm"
                        onClick={addSelected}
                        disabled={picked.size === 0}
                      >
                        <Icon icon="mdi:plus" /> Add {picked.size || ""}
                      </button>
                    </div>
                  </div>

                  {results.length === 0 ? (
                    <div className="gmb-empty">
                      Nothing found. Try a simpler word, or a wider distance.
                    </div>
                  ) : (
                    results.map((r) => (
                      <label
                        className={`in-result ${
                          picked.has(r.placeId) ? "picked" : ""
                        } ${r.alreadyAdded ? "added" : ""}`}
                        key={r.placeId}
                      >
                        <input
                          type="checkbox"
                          checked={picked.has(r.placeId)}
                          disabled={r.alreadyAdded}
                          onChange={() => togglePick(r.placeId)}
                        />
                        <div className="in-result-body">
                          <b>{r.name}</b>
                          <div className="in-result-meta">
                            {r.category && <span>{r.category}</span>}
                            {r.distanceKm != null && (
                              <span>{r.distanceKm} km away</span>
                            )}
                            {r.address && (
                              <span className="in-result-addr">
                                {r.address.slice(0, 46)}
                              </span>
                            )}
                          </div>
                          {r.warning && (
                            <div className="in-result-warn">
                              <Icon icon="mdi:alert-outline" /> {r.warning}
                            </div>
                          )}
                        </div>
                        <span className="in-result-rating">
                          {r.rating || "—"} ★
                          <small>{compact(r.reviewCount)} reviews</small>
                        </span>
                        {r.alreadyAdded && (
                          <span className="in-result-tag">Already added</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              )}

              {radar.searches?.length > 0 && (
                <div className="in-searches">
                  {radar.searches.map((s) => (
                    <div className="in-search-card" key={s.id}>
                      <div className="in-search-card-head">
                        <span className="in-search-term">
                          <Icon icon="mdi:magnify" /> {s.keyword}
                        </span>
                        <button
                          className="in-search-remove"
                          onClick={() => dropSearch(s.id)}
                          title={`Remove the ${s.businesses.length} business${
                            s.businesses.length === 1 ? "" : "es"
                          } this search added`}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                      <div className="in-search-names">
                        {s.businesses.map((b) => (
                          <span key={b.id}>
                            {b.name}
                            {b.rating ? ` · ${b.rating} ★` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ---- collection status, in plain words ---- */}
              {radar.competitors.length > 0 && (
                <div className={`in-collecting ${sync?.isRunning ? "" : "idle"}`}>
                  {sync?.isRunning ? (
                    <>
                      <span className="in-spinner" />
                      <span>
                        Reading reviews from {radar.competitors.length} nearby
                        businesses. This page will update on its own.
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:store-check-outline" style={{ fontSize: 17 }} />
                      <span>
                        Watching {radar.competitors.length} businesses near you.
                        {radar.health.maturity === "early" &&
                          " Google only gives us a few reviews at a time, so this gets more accurate each week."}
                      </span>
                      {sync?.hasRun && (
                        <span className="in-collecting-side">
                          Last checked {relativeTime(sync.lastRunAt)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ---- the findings ---- */}
              {!advice.comparison.hasComparison ? (
                <div className="in-empty-block">
                  <div className="in-empty-icon">
                    <Icon icon="mdi:store-search-outline" />
                  </div>
                  <b>No nearby businesses added yet</b>
                  <p>
                    Search above and we will show you where they are doing better
                    than you, and where you are already ahead.
                  </p>
                </div>
              ) : advice.comparison.findings.length === 0 ? (
                <div className="in-empty-block">
                  <div className="in-empty-icon">
                    <Icon icon="mdi:clock-outline" />
                  </div>
                  <b>Still collecting</b>
                  <p>{advice.comparison.note}</p>
                </div>
              ) : (
                <div className="row g-3">
                  {advice.comparison.findings.map((f) => (
                    <div className="col-xl-6" key={f.id}>
                      <Finding f={f} onEvidence={openEvidence} />
                    </div>
                  ))}
                </div>
              )}

              {/* ---- the numbers ---- */}
              {advice.comparison.hasComparison && (
                <>
                  <button
                    className="in-detail-toggle"
                    onClick={() => toggleDetail("compare")}
                  >
                    <Icon icon={openDetail.compare ? "mdi:chevron-up" : "mdi:chevron-down"} />
                    {openDetail.compare
                      ? "Hide the full comparison"
                      : "See the full comparison table"}
                  </button>

                  {openDetail.compare && (
                    <div className="in-detail-panel">
                      <div className="row g-3 mb-3">
                        <div className="col-xl-5">
                          <div className="gmb-card in-panel">
                            <div className="in-panel-title">Star ratings nearby</div>
                            <div className="in-panel-sub">
                              Everyone we are comparing you with.
                            </div>
                            {comparison.headline.ratingLadder.map((r) => (
                              <div className="in-comp-row" key={`${r.name}-${r.rank}`}>
                                <div className="in-comp-info">
                                  <b style={r.isMine ? { color: "var(--primary-700)" } : {}}>
                                    {r.rank}. {r.name}
                                  </b>
                                </div>
                                <span className="in-comp-rating">{r.rating} ★</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="col-xl-7">
                          <div className="gmb-card in-panel">
                            <div className="in-panel-title">
                              Who has more of each complaint
                            </div>
                            <div className="in-panel-sub">
                              Out of every 100 reviews, how many mention a problem
                              with each thing. Darker means more complaints.
                            </div>
                            <div className="in-heatmap-wrap">
                              <table className="in-heatmap">
                                <thead>
                                  <tr>
                                    <th className="corner">Business</th>
                                    {comparison.themeList.map((t) => (
                                      <th key={t.key}>{t.short}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {comparison.matrix.map((row) => (
                                    <tr key={row.id}>
                                      <td className="name">{row.name}</td>
                                      {comparison.themeList.map((t) => {
                                        const cell = row.themes[t.key] || { rate: 0, count: 0 };
                                        return (
                                          <td
                                            key={t.key}
                                            className={`in-cell ${row.hasSignal ? "" : "thin"}`}
                                            style={{ background: heatColor(cell.rate) }}
                                            title={`${row.name} — ${t.label}: ${cell.count} complaints`}
                                          >
                                            {cell.rate ? `${cell.rate}%` : "—"}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}

        {/* ==================== POST PREVIEW ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={!!preview}
          onHide={() => setPreview(null)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Your post</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {preview && (
              <>
                <div className="in-quote-block positive">
                  <p>"{preview.quote}"</p>
                  <div className="in-quote-meta">
                    <b>{preview.attribution || "A happy customer"}</b>
                    <span className="in-stars">{stars(preview.rating)}</span>
                    <span>{fmtDate(preview.reviewDate)}</span>
                  </div>
                </div>

                <div className="in-caption-box">
                  <b>Ready to paste</b>
                  {preview.caption}
                </div>

                <div className="in-hashtags mt-3">{preview.hashtags}</div>

                <div className="in-panel-sub mt-3 mb-0">
                  Add a photo of whatever the customer is praising. A quote on a
                  plain background looks like an advertisement; a quote over the
                  actual dish or room looks like proof.
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setPreview(null)}
            >
              Close
            </button>
            <button
              className="btn btn-primary-600"
              onClick={() => {
                copyCard(preview);
                setPreview(null);
              }}
            >
              <Icon icon="mdi:content-copy" /> Copy it
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== EVIDENCE ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showEvidence}
          onHide={() => setShowEvidence(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>
              {evidence?.label
                ? `What people wrote about ${evidence.label.toLowerCase()}`
                : "What people wrote"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!evidence ? (
              <div className="gmb-empty">Loading...</div>
            ) : (
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="in-panel-title">
                    Your reviews ({evidence.myCount})
                  </div>
                  <div className="in-panel-sub">Written by your own customers.</div>
                  {evidence.mine.length === 0 ? (
                    <div className="gmb-empty">
                      Nobody has mentioned this in your reviews.
                    </div>
                  ) : (
                    evidence.mine.map((q, i) => (
                      <div className={`in-quote-block ${q.polarity}`} key={i}>
                        <p>"{q.excerpt}"</p>
                        <div className="in-quote-meta">
                          <span className="in-stars">{stars(q.rating)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="col-md-6">
                  <div className="in-panel-title">
                    Nearby businesses ({evidence.theirCount})
                  </div>
                  <div className="in-panel-sub">
                    Public reviews from businesses around you.
                  </div>
                  {evidence.theirs.length === 0 ? (
                    <div className="gmb-empty">
                      Nothing collected about this yet.
                    </div>
                  ) : (
                    evidence.theirs.map((q, i) => (
                      <div className={`in-quote-block ${q.polarity}`} key={i}>
                        <p>"{q.excerpt}"</p>
                        <div className="in-quote-meta">
                          <b>{q.business}</b>
                          <span className="in-stars">{stars(q.rating)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== MANAGE LIST ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showCompetitors}
          onHide={() => setShowCompetitors(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Businesses we are watching</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!radar?.competitors?.length ? (
              <div className="gmb-empty">Nothing added yet.</div>
            ) : (
              radar.competitors.map((c) => (
                <div className="in-comp-row" key={c.id}>
                  <div className="in-comp-info">
                    <b>{c.name}</b>
                    {c.addedByKeyword && (
                      <span className="in-keyword-tag">{c.addedByKeyword}</span>
                    )}
                    <div className="in-comp-meta">
                      {c.distanceKm != null && `${c.distanceKm} km away`}
                      {c.address && ` · ${c.address.slice(0, 45)}`}
                    </div>
                  </div>
                  <span className="in-comp-rating">
                    {c.rating || "—"} ★
                    <small>{compact(c.reviewCount)} reviews</small>
                  </span>
                  <button
                    className="btn btn-outline-danger-600 btn-sm"
                    onClick={() => dropCompetitor(c.id)}
                    title="Stop comparing with this one"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default IntelligenceDashboard;